// Native (app-build) bearer token store — the SINGLE source of truth for the
// access/refresh token pair the app authenticates with.
//
// WHY the interface is ASYNC (getTokens/setTokens/clearTokens all return
// Promises): the native backend is the iOS Keychain via the Capacitor
// secure-storage plugin, whose API is async. The interface was made async in
// F4.2b precisely so this F4.2c swap would touch only this file — the consumers
// (lib/api.js, contexts/AuthContext.js) already `await` every call.
//
// TWO BACKENDS, decided at call time per operation:
//   - Native platform (the Capacitor iOS shell): the secure-storage plugin
//     (iOS Keychain). Detected via `window.Capacitor?.isNativePlatform?.()` —
//     the global the Capacitor runtime injects into its WebView — instead of
//     importing @capacitor/core here, so this module stays dependency-free for
//     the web bundle. The plugin itself is pulled in with a DYNAMIC import()
//     inside the native branch: webpack splits it into a lazy chunk that a
//     browser never requests (zero new dead weight in the web bundle).
//   - Everywhere else (app-mode build opened in a browser, Jest/jsdom): the
//     original localStorage implementation, unchanged. In the WEB build every
//     consumer guards its store calls behind IS_APP_BUILD (runtime false), so
//     none of this ever runs there — web behavior, cookies only, is unchanged.
//
// KEYCHAIN LIFETIME (known behavior, by design): the iOS Keychain SURVIVES app
// uninstall. After a delete + reinstall, getTokens() can return a refresh token
// that the backend has since revoked or expired. That is handled by the EXISTING
// F4.2b restore path, not here: AuthContext.fetchUser posts the stored token to
// /auth/refresh and on failure clears the store and stays logged out
// (AuthContext.js — catch → clearTokens() → setUser(null)); lib/api.js does the
// same on an unrecoverable in-session refresh failure. The store just reports
// what the Keychain holds.
//
// Everything lives under ONE constant key — never sprinkle the literal around.

const TOKEN_STORE_KEY = 'sb_native_auth';

// True only inside a real Capacitor native shell (the runtime injects
// window.Capacitor into its WebView). Evaluated per call, not at module load:
// jsdom tests install/remove the global between cases, and at module-eval time
// during app startup the bridge is already present anyway.
function isNativePlatform() {
  return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
}

// Hard ceiling on every native Keychain operation. Keychain calls complete in
// milliseconds; if the bridge ever fails to answer (wedged plugin, future
// regression), the op must DEGRADE (get → null, set/clear → best-effort no-op)
// instead of holding the boot spinner forever. 5s is generous for a local
// syscall while still letting a broken vault fall through to a clean /login.
const NATIVE_KEYCHAIN_TIMEOUT_MS = 5000;

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      setTimeout(
        () => reject(new Error(`${label} timed out after ${NATIVE_KEYCHAIN_TIMEOUT_MS}ms`)),
        NATIVE_KEYCHAIN_TIMEOUT_MS
      );
    }),
  ]);
}

// Lazy accessor for the secure-storage plugin (iOS Keychain). Dynamic import —
// see the module docstring for why. The import is memoized by the module system
// itself; no extra caching needed.
//
// RETURNS A WRAPPER OBJECT, NEVER THE PLUGIN ITSELF — this is load-bearing.
// `SecureStorage` is a Capacitor registerPlugin PROXY that turns EVERY property
// access into a native method call, including `then`. If the raw proxy becomes
// the resolution value of any promise (e.g. `return SecureStorage` from an
// async function), the JS engine's thenable-assimilation step reads `.then`,
// the proxy dispatches a native call `SecureStorage.then()`, iOS rejects it
// with "not implemented" (as an unhandled rejection, since the proxy discards
// the assimilation callbacks) and the awaiting promise NEVER settles. That was
// the F4.2c boot hang on device. Wrapping in `{ plugin }` makes the resolution
// value a plain object with no `then`, so assimilation is a no-op.
async function getSecureStorage() {
  const mod = await import('@aparajita/capacitor-secure-storage');
  return { plugin: mod.SecureStorage };
}

// Persist the bearer pair from a successful native auth response
// (login / verify / guest / conversion / refresh — all return the F3
// `_bearer_token_payload` fields). We project to exactly the fields we consume:
// `access_token` (attached as the Authorization header by the request
// interceptor), `refresh_token` (sent in the body of the native refresh/logout
// calls), and `token_type` (the F3 contract always returns "Bearer"; we echo it
// back instead of hardcoding).
//
// GUARD: a payload without an access_token is IGNORED (no-op). This protects the
// store from being clobbered by a response that carries no tokens.
export async function setTokens(payload) {
  if (!payload || !payload.access_token) return;
  const tokens = {
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
    token_type: payload.token_type || 'Bearer',
  };
  try {
    if (isNativePlatform()) {
      // setItem (string in / string out) mirrors the localStorage branch — one
      // JSON shape for both backends, no plugin-side (de)serialization quirks.
      await withTimeout(
        (async () => {
          const { plugin } = await getSecureStorage();
          await plugin.setItem(TOKEN_STORE_KEY, JSON.stringify(tokens));
        })(),
        'Keychain setItem'
      );
    } else {
      localStorage.setItem(TOKEN_STORE_KEY, JSON.stringify(tokens));
    }
  } catch (e) {
    // Storage may be unavailable (Keychain OS error / hang past the timeout /
    // private mode / quota) — best-effort, never throw.
  }
}

// Read the stored bearer pair, or null if none / unreadable.
export async function getTokens() {
  try {
    let raw;
    if (isNativePlatform()) {
      raw = await withTimeout(
        (async () => {
          const { plugin } = await getSecureStorage();
          return plugin.getItem(TOKEN_STORE_KEY);
        })(),
        'Keychain getItem'
      );
    } else {
      raw = localStorage.getItem(TOKEN_STORE_KEY);
    }
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // Any vault failure — including a hang caught by the timeout — degrades to
    // "no session": the caller lands on /login instead of a stuck spinner.
    return null;
  }
}

// Drop the stored bearer pair (logout, or an unrecoverable refresh failure —
// including the stale-Keychain-after-reinstall case described above).
export async function clearTokens() {
  try {
    if (isNativePlatform()) {
      await withTimeout(
        (async () => {
          const { plugin } = await getSecureStorage();
          await plugin.removeItem(TOKEN_STORE_KEY);
        })(),
        'Keychain removeItem'
      );
    } else {
      localStorage.removeItem(TOKEN_STORE_KEY);
    }
  } catch (e) {
    // Best-effort — nothing to do if storage is unavailable.
  }
}

// Exported for tests only (asserting the single, constant key name and driving
// the hang-degradation test with fake timers).
export const _TOKEN_STORE_KEY = TOKEN_STORE_KEY;
export const _NATIVE_KEYCHAIN_TIMEOUT_MS = NATIVE_KEYCHAIN_TIMEOUT_MS;
